// Servicer på verkstedet (/verkstedet): forenklet oversikt for butikkansatte over servicene som er
// inne, sortert på forventet ferdigdato (uten dato til slutt). Til å svare kunder som ringer.
// Bare informasjon; Servicelisten (/workshop) og WorkshopDetails er uendret. Se verkstedet/felles.js.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tilbake, Sidetopp, Valgknapper, feltKlasse } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import {
  LUKKET, STATUS_FARGE, statusNavn, butikkFor, valgtButikk, ferdigDato, leveringsDato,
  kundeNavn, produktTekst, dagerTil, narTekst, hentServicer,
} from '../verkstedet/felles';

const BUTIKK_VALG = [
  { verdi: 'Røyken', tittel: 'Røyken' },
  { verdi: 'Slemmestad', tittel: 'Slemmestad' },
  { verdi: 'Alle', tittel: 'Begge' },
];

// Bare sifre uten landkode, så «+47 400 00 000» og «40000000» er like.
function sifre(s) {
  const d = String(s || '').replace(/\D/g, '');
  if (d.length > 8 && d.startsWith('0047')) return d.slice(4);
  if (d.length > 8 && d.startsWith('47')) return d.slice(2);
  return d;
}

function passer(s, sok) {
  const q = sok.trim().toLowerCase();
  if (!q) return true;
  const tall = sifre(q);
  if (/^[\d\s+]+$/.test(q) && tall.length >= 3) {
    return sifre(s.customerSnapshot?.phoneNumber).includes(tall) || String(s.serviceRef || '').includes(tall);
  }
  const tekst = [kundeNavn(s), produktTekst(s), s.serviceRef, s.customerSnapshot?.email].join(' ').toLowerCase();
  return q.split(/\s+/).every((ord) => tekst.includes(ord));
}

function ServiceRad({ s }) {
  const ferdig = ferdigDato(s);
  const levert = leveringsDato(s);
  const passert = ferdig && dagerTil(ferdig) < 0;
  const arbeid = (s.serviceLines || []).map((l) => l.navn).filter(Boolean);
  const status = String(s.status || '').toLowerCase();
  return (
    <li>
      <Link
        to={`/verkstedet/${s._id}`}
        state={{ service: s }}
        className="group flex flex-wrap items-start gap-x-3 gap-y-2 px-3 py-1.5 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-600/50"
      >
        <div className={`w-28 shrink-0 rounded-xl px-3 py-1.5 text-center ${!ferdig ? 'bg-slate-100' : passert ? 'bg-amber-100' : 'bg-emerald-50'}`}>
          <span className="block text-xs font-semibold uppercase tracking-wide text-slate-600">Ferdig</span>
          <span className={`block font-bold first-letter:uppercase ${passert ? 'text-amber-900' : 'text-slate-900'}`}>
            {ferdig ? narTekst(ferdig) : 'Ikke satt'}
          </span>
          {passert && <span className="block text-xs font-medium text-amber-900">Dato passert</span>}
        </div>

        <div className="min-w-0 flex-1 basis-56">
          <p className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-slate-900 [overflow-wrap:anywhere]">{produktTekst(s)}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${STATUS_FARGE[status] || 'bg-slate-100 text-slate-800'}`}>{statusNavn(s.status)}</span>
            {s.produkt?.prioritering?.haster && (
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-semibold text-red-900">Haster</span>
            )}
          </p>
          <p className="text-slate-800 [overflow-wrap:anywhere]">
            {kundeNavn(s) || 'Ukjent kunde'}
            <span className="text-slate-600"> · <span className="font-mono">{s.customerSnapshot?.phoneNumber || 'uten telefon'}</span></span>
          </p>
          <p className="text-sm text-slate-600 [overflow-wrap:anywhere]">
            {[arbeid.length ? arbeid.join(', ') : 'Ingen arbeid lagt inn', levert && `levert ${narTekst(levert)}`, s.serviceRef && `ref. ${s.serviceRef}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {s.produkt?.kommentar && (
            <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-700">
              <Ikon navn="melding" className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              <span className="line-clamp-2 [overflow-wrap:anywhere]">{s.produkt.kommentar}</span>
            </p>
          )}
        </div>
        <Ikon navn="pil" className="mt-2 h-5 w-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
      </Link>
    </li>
  );
}

export default function ServicerPaVerkstedet() {
  const [servicer, setServicer] = useState([]);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState('');
  const [butikk, setButikk] = useState(() => valgtButikk() || 'Alle');
  const [sok, setSok] = useState('');

  useEffect(() => {
    let aktiv = true;
    hentServicer()
      .then((liste) => { if (aktiv) setServicer(liste.filter((s) => !LUKKET.includes(String(s.status || '').toLowerCase()))); })
      .catch((e) => {
        console.error('Feil ved henting av servicer:', e);
        if (aktiv) setFeil('Klarte ikke å hente servicene. Last siden på nytt om litt.');
      })
      .finally(() => { if (aktiv) setLastet(true); });
    return () => { aktiv = false; };
  }, []);

  // Forventet ferdigdato først, de uten dato til slutt; ellers eldste innlevering først.
  const liste = useMemo(
    () => servicer
      .filter((s) => butikk === 'Alle' || butikkFor(s) === butikk)
      .filter((s) => passer(s, sok))
      .sort((a, b) => {
        const fa = ferdigDato(a);
        const fb = ferdigDato(b);
        if (fa && fb && fa - fb) return fa - fb;
        if (fa && !fb) return -1;
        if (!fa && fb) return 1;
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }),
    [servicer, butikk, sok]
  );

  const utenDato = liste.filter((s) => !ferdigDato(s)).length;

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp
        ikon="info"
        farge="verksted"
        tittel="Serviceliste"
        undertittel="Hva som er inne, når det er ferdig og status. Til kunder som ringer."
      />

      <section aria-label="Søk og utvalg" className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div>
          <label htmlFor="sv-sok" className="mb-1 block font-semibold text-slate-800">Finn kunden eller servicen</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <Ikon navn="sok" className="w-6 h-6" />
            </span>
            <input
              id="sv-sok"
              type="search"
              inputMode="search"
              autoComplete="off"
              value={sok}
              onChange={(e) => setSok(e.target.value)}
              placeholder="Navn, telefon, sykkel eller ref."
              aria-describedby="sv-sok-status"
              className={`${feltKlasse} pl-12`}
            />
          </div>
          <p id="sv-sok-status" role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
            {lastet && sok.trim() ? `${liste.length} ${liste.length === 1 ? 'service' : 'servicer'} passer` : ''}
          </p>
        </div>
        <Valgknapper id="sv-butikk" etikett="Butikk" valg={BUTIKK_VALG} verdi={butikk} onVelg={setButikk} />
      </section>

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      <section aria-labelledby="sv-liste" className="space-y-2">
        <h2 id="sv-liste" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
          Inne nå{butikk !== 'Alle' ? ` i ${butikk}` : ''}
          {lastet && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-semibold text-emerald-900">{liste.length}</span>}
        </h2>
        {lastet && liste.length > 0 && (
          <p className="text-sm text-slate-600">
            Sortert etter forventet ferdigdato.{utenDato > 0 ? ` ${utenDato} uten ferdigdato står til slutt.` : ''}
          </p>
        )}
        {!lastet ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter servicer …</p>
        ) : liste.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
            {sok.trim() ? 'Ingen servicer passer søket. Prøv «Begge» butikker, eller et annet navn eller nummer.' : 'Ingen servicer er inne nå.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {liste.map((s) => <ServiceRad key={s._id} s={s} />)}
          </ul>
        )}
      </section>
    </div>
  );
}
